/**
 * Sstv2Migration entity (DEV-193).
 *
 * Tracks the SirSteward (SST) V1 -> V2 locker migration lifecycle per source
 * locker. One record per `sl1LockerId` within a tenant. Phase is folded
 * forward from the `migration:*` SQS event family by event `timestamp`
 * (events arrive out of order; rollback is the only backward transition).
 */
export const Sstv2MigrationThingTypeMrn = "mrn:sl2:::sstv2Migration";

export const Sstv2MigrationPhaseList = [
  "ACTIVE_MIGRATED",
  "OPERATIONAL",
  "COMPLETED",
  "ROLLBACK_STARTED",
  "ROLLED_BACK",
] as const;

export const Sstv2MigrationPhaseEnum = {
  ACTIVE_MIGRATED: "ACTIVE_MIGRATED",
  OPERATIONAL: "OPERATIONAL",
  COMPLETED: "COMPLETED",
  ROLLBACK_STARTED: "ROLLBACK_STARTED",
  ROLLED_BACK: "ROLLED_BACK",
} as const;

export type Sstv2MigrationPhase = (typeof Sstv2MigrationPhaseList)[number];

/**
 * OUR migration-JOB processing state machine (DEV-193, scale redesign §4.3) —
 * distinct from the partner-derived `phase` above. `phase` is folded from the
 * partner's `migration:*` event timeline; `processingState` reflects where OUR
 * pipeline (ingestion fan-out → chunk processor) has reached for this job. The
 * admin UI shows BOTH.
 *
 * Forward ladder:
 *   INGESTING            — receiving/fanning-out reservation batches
 *   MATERIALIZING_ACTIVE — processing ACTIVE reservation chunks
 *   OPERATIONAL          — V1→V2 flipped (partner operational event received)
 *   DRAINING_HISTORY     — active done; the 30k historical drain runs in bg
 *   COMPLETE             — both streams fully processed + reconciled
 *
 * Rollback variants mirror the partner-`phase` rollback handling
 * (`Sstv2MigrationRollbackPhases`): a rollback event drives the job's own state
 * backward regardless of forward progress.
 */
export const Sstv2MigrationProcessingStateList = [
  "INGESTING",
  "MATERIALIZING_ACTIVE",
  "OPERATIONAL",
  "DRAINING_HISTORY",
  "COMPLETE",
  "ROLLBACK_STARTED",
  "ROLLED_BACK",
] as const;

export const Sstv2MigrationProcessingStateEnum = {
  INGESTING: "INGESTING",
  MATERIALIZING_ACTIVE: "MATERIALIZING_ACTIVE",
  OPERATIONAL: "OPERATIONAL",
  DRAINING_HISTORY: "DRAINING_HISTORY",
  COMPLETE: "COMPLETE",
  ROLLBACK_STARTED: "ROLLBACK_STARTED",
  ROLLED_BACK: "ROLLED_BACK",
} as const;

export type Sstv2MigrationProcessingState =
  (typeof Sstv2MigrationProcessingStateList)[number];

/** Processing states that represent (or follow) a rollback. */
export const Sstv2MigrationProcessingRollbackStates: ReadonlySet<Sstv2MigrationProcessingState> =
  new Set<Sstv2MigrationProcessingState>(["ROLLBACK_STARTED", "ROLLED_BACK"]);

/**
 * Processing states at which the migration JOB is TERMINAL — no more reservation
 * batches will be attributed to it, so `findInflightSstv2Migrations` excludes
 * these candidates. `ROLLBACK_STARTED` is deliberately NOT terminal: a rollback
 * can still be receiving/undoing batch work, so it stays in-flight. (DEV-193 —
 * in-flight migration probe for the pre-existing-v1-row match.)
 */
export const Sstv2MigrationTerminalProcessingStates: ReadonlySet<Sstv2MigrationProcessingState> =
  new Set<Sstv2MigrationProcessingState>(["COMPLETE", "ROLLED_BACK"]);

/**
 * Forward-progress ladder for OUR `processingState`. Ingestion/processor code
 * only advances DOWN this list (never regresses), so a late/out-of-order event
 * can't drag the state backward. Rollback states are excluded — they are applied
 * unconditionally elsewhere (the only allowed backward transition).
 */
export const Sstv2MigrationProcessingStateForwardOrder: readonly Sstv2MigrationProcessingState[] =
  [
    "INGESTING",
    "MATERIALIZING_ACTIVE",
    "OPERATIONAL",
    "DRAINING_HISTORY",
    "COMPLETE",
  ];

/**
 * True if `to` is strictly forward of `from` on the processing ladder — i.e. the
 * caller should advance `processingState` to `to`. Rules:
 *  - a rollback `from` never advances (rollback is terminal for forward progress);
 *  - an undefined `from` is treated as the ladder start (`INGESTING`), so any
 *    real forward target advances;
 *  - a `to` off the forward ladder (e.g. a rollback target) returns false —
 *    rollback transitions are driven by the rollback handlers, not this helper.
 */
export const isForwardProcessingState = (
  from: Sstv2MigrationProcessingState | undefined,
  to: Sstv2MigrationProcessingState,
): boolean => {
  if (from && Sstv2MigrationProcessingRollbackStates.has(from)) return false;
  const toRank = Sstv2MigrationProcessingStateForwardOrder.indexOf(to);
  if (toRank < 0) return false;
  const fromRank = from
    ? Sstv2MigrationProcessingStateForwardOrder.indexOf(from)
    : 0;
  return toRank > fromRank;
};

/**
 * Forward-progress rank of each phase. A higher rank supersedes a lower one
 * when folding out-of-order events. Rollback phases are NOT ranked above the
 * forward phases — they are applied unconditionally (the only allowed
 * backward transition), so they are excluded from the forward ladder.
 */
export const Sstv2MigrationPhaseRank: Record<Sstv2MigrationPhase, number> = {
  ACTIVE_MIGRATED: 1,
  OPERATIONAL: 2,
  COMPLETED: 3,
  ROLLBACK_STARTED: 4,
  ROLLED_BACK: 5,
};

/** Phases that represent (or follow) a rollback. */
export const Sstv2MigrationRollbackPhases: ReadonlySet<Sstv2MigrationPhase> =
  new Set<Sstv2MigrationPhase>(["ROLLBACK_STARTED", "ROLLED_BACK"]);

/**
 * Partner-derived phases at which the migration is TERMINAL. Mirrors
 * `Sstv2MigrationTerminalProcessingStates` for the partner `phase` axis — a
 * record whose phase is COMPLETED or ROLLED_BACK is excluded from the in-flight
 * candidate set. `ROLLBACK_STARTED` stays in-flight (rollback still underway).
 */
export const Sstv2MigrationTerminalPhases: ReadonlySet<Sstv2MigrationPhase> =
  new Set<Sstv2MigrationPhase>(["COMPLETED", "ROLLED_BACK"]);

/**
 * Authoritative map from a Shape-A migration event TYPE to the phase it implies.
 * The phase of a migration record is derived from the latest event in its
 * (timestamp-sorted) timeline via this map — see `derivePhaseFromEvents`. This
 * is the single source of truth for event→phase; the per-handler `foldMigrationEvent`
 * phase arguments must agree with it.
 *
 * Keys are the wire tokens from `Sstv2EventTypeEnum` (kept as string literals to
 * avoid coupling this shared module to the partner event-schema module). Both
 * completion tokens (current + legacy alias) map to COMPLETED. Shape-B
 * reservation-batch events never fold into this entity, so they are absent.
 */
export const Sstv2MigrationEventTypeToPhase: Record<string, Sstv2MigrationPhase> =
  {
    "migration:locker:migrated": "ACTIVE_MIGRATED",
    "migration:locker:operational": "OPERATIONAL",
    "migration:reservations:history:completed": "COMPLETED",
    "migration:locker:completed": "COMPLETED",
    "migration:rollback:started": "ROLLBACK_STARTED",
    "migration:rollback:done": "ROLLED_BACK",
  };

/** True if an event token belongs to the rollback family. */
export const isRollbackMigrationEvent = (eventType: string): boolean =>
  eventType.startsWith("migration:rollback:");

/**
 * Status summary mirroring the partner `GET /migration/status/{uuid}` shape
 * (`{ status, started_at, finished_at, last_step }`). We DERIVE it from the
 * locally-folded entity so the admin UI speaks the same vocabulary as the
 * partner and the two can be reconciled. `status` is our own normalized enum
 * (the partner's raw `status` string — e.g. `"DONE"` — is loose); timestamps
 * are the partner naive-UTC values straight from the folded events.
 */
export type Sstv2MigrationStatusValue =
  | "NOT_STARTED"
  | "IN_PROGRESS"
  | "DONE"
  | "ROLLBACK_IN_PROGRESS"
  | "ROLLED_BACK";

export type Sstv2MigrationStatusView = {
  status: Sstv2MigrationStatusValue;
  started_at?: string;
  finished_at?: string;
  last_step?: string;
};

const PHASE_TO_STATUS: Record<Sstv2MigrationPhase, Sstv2MigrationStatusValue> = {
  ACTIVE_MIGRATED: "IN_PROGRESS",
  // `operational` is the official cutover; the partner reports DONE from here.
  OPERATIONAL: "DONE",
  COMPLETED: "DONE",
  ROLLBACK_STARTED: "ROLLBACK_IN_PROGRESS",
  ROLLED_BACK: "ROLLED_BACK",
};

/** Statuses at which the migration has reached a terminal point. */
const TERMINAL_STATUSES: ReadonlySet<Sstv2MigrationStatusValue> = new Set([
  "DONE",
  "ROLLED_BACK",
]);

/**
 * Derive the partner-shaped status view from a folded migration entity (or any
 * structurally-compatible object — the wfe row works too). Pass `null`/empty
 * for a not-yet-started locker.
 */
export const deriveMigrationStatus = (
  m: {
    phase?: Sstv2MigrationPhase | null;
    lastEventType?: string | null;
    lastEventAt?: string | null;
    events?: ReadonlyArray<{ timestamp: string }> | null;
  } | null,
): Sstv2MigrationStatusView => {
  if (!m || !m.phase) return { status: "NOT_STARTED" };

  const status = PHASE_TO_STATUS[m.phase];
  const startedAt = (m.events ?? [])
    .map((e) => e.timestamp)
    .filter(Boolean)
    .sort()[0];

  return {
    status,
    started_at: startedAt,
    finished_at: TERMINAL_STATUSES.has(status)
      ? (m.lastEventAt ?? undefined)
      : undefined,
    last_step: m.lastEventType ?? undefined,
  };
};
